import {
  ArrowDropDown as ArrowDropDownIcon,
  Check as CheckIcon,
  Code as CodeIcon,
  CompareArrows as CompareArrowsIcon,
  List as ListIcon,
  MoreVert as MoreVertIcon,
  PlayArrow as PlayArrowIcon,
  Redo as RedoIcon,
  Save as SaveIcon,
  CalendarMonth as ScheduleIcon,
  Search as SearchIcon,
  Storage as StorageIcon,
  Undo as UndoIcon,
  AccountTree as VisualIcon,
} from '@mui/icons-material';
import type { SelectChangeEvent } from '@mui/material';
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Tooltip,
} from '@mui/material';

import { useRecipeEditor } from '../context/RecipeEditorContext';

interface RecipeEditorToolbarProps {
  onUndo: () => void;
  onRedo: () => void;
  onSave: () => void;
  onSaveMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  onDiscardChanges: () => void;
  onRun: (mode: 'runAsJob' | 'runAsEvent') => void;
  onRunMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  onDeploy: () => void;
  onVersionsClick: () => void;
  onCheckInClick: () => void;
  onToolsMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  onScheduleClick: () => void;
  onCommandPaletteClick: () => void;
  onEditorModeChange: (mode: 'visual' | 'yaml') => void;
  onExecutionLogToggle: () => void;
  onCompareToggle: () => void;
  onMetadataToggle: () => void;
  onEditMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  onViewMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  onToggleComment: () => void;
  onFormatDocument: () => void;
  onFoldSchemas: () => void;
  onToggleMinimap: () => void;
  onOpenSqlEditor: () => void;
  onEnvironmentChange: (event: SelectChangeEvent<string>) => void;
  onToggleAutosave: () => void;
  onSwapJobType: () => void;
  saveMenuAnchor: HTMLElement | null;
  runMenuAnchor: HTMLElement | null;
  toolsMenuAnchor: HTMLElement | null;
  templateMenuAnchor: HTMLElement | null;
  editMenuAnchor: HTMLElement | null;
  viewMenuAnchor: HTMLElement | null;
  environments: Array<{ id: string; name: string; color?: string; deployedVersion?: number }>;
  isDeployedToSelectedEnv?: boolean;
}

export const RecipeEditorToolbar: React.FC<RecipeEditorToolbarProps> = ({
  onUndo,
  onRedo,
  onSave,
  onSaveMenuOpen,
  onDiscardChanges,
  onRun,
  onRunMenuOpen,
  onDeploy,
  onVersionsClick,
  onCheckInClick,
  onToolsMenuOpen,
  onScheduleClick,
  onCommandPaletteClick,
  onEditorModeChange,
  onExecutionLogToggle,
  onCompareToggle,
  onMetadataToggle,
  onEnvironmentChange,
  onToggleAutosave,
  saveMenuAnchor,
  runMenuAnchor,
  toolsMenuAnchor,
  environments,
  isDeployedToSelectedEnv,
}) => {
  const { state, dispatch } = useRecipeEditor();

  const {
    canUndo,
    canRedo,
    isSaving,
    isModified,
    isReadOnly,
    jobMode,
    isExecuting,
    selectedEnvironment,
    isDeploying,
    currentVersion,
    isLatestVersion: _isLatestVersion,
    showTemplateSelection,
    editorMode,
    showCompare,
    autosaveEnabled,
    isSwitchingMode,
    isSwitchingRecipe,
  } = state;

  const handleSaveMenuClose = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Synthetic event for menu close
    const event = new MouseEvent('click') as any;
    onSaveMenuOpen(event);
  };

  const handleRunMenuClose = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Synthetic event for menu close
    const event = new MouseEvent('click') as any;
    onRunMenuOpen(event);
  };

  const handleToolsMenuClose = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Synthetic event for menu close
    const event = new MouseEvent('click') as any;
    onToolsMenuOpen(event);
  };

  return (
    <Box
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        flexShrink: 0,
        overflowX: 'auto',
        overflowY: 'hidden',
        // Hide scrollbar but keep scroll functionality
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      <Toolbar
        variant="dense"
        sx={{
          minHeight: 40,
          height: 40,
          gap: 0.5,
          pl: 1,
          pr: 1.5,
          flexWrap: 'nowrap',
          minWidth: 'max-content', // Prevent items from shrinking
          '& .MuiButton-root': {
            height: 28,
            minHeight: 28,
            px: 1,
            fontSize: '12px',
            textTransform: 'none',
            whiteSpace: 'nowrap',
          },
          '& .MuiIconButton-root': {
            padding: '4px',
          },
          '& .MuiToggleButton-root': {
            height: 28,
            px: 1,
            fontSize: '12px',
            textTransform: 'none',
            whiteSpace: 'nowrap',
          },
        }}
      >
        {/* LEFT SECTION: Undo/Redo + Save */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
          <Tooltip title="Undo (Cmd+Z)">
            <span>
              <IconButton
                size="small"
                disabled={!canUndo || isReadOnly || isSwitchingMode || isSwitchingRecipe}
                onClick={onUndo}
              >
                <UndoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Redo (Cmd+Shift+Z)">
            <span>
              <IconButton
                size="small"
                disabled={!canRedo || isReadOnly || isSwitchingMode || isSwitchingRecipe}
                onClick={onRedo}
              >
                <RedoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <ButtonGroup size="small" variant="outlined">
            <Button
              startIcon={isSaving ? <CircularProgress size={14} /> : <SaveIcon />}
              disabled={
                !isModified || isSaving || isReadOnly || isSwitchingMode || isSwitchingRecipe
              }
              onClick={onSave}
            >
              Save
            </Button>
            <Button
              size="small"
              onClick={onSaveMenuOpen}
              disabled={isReadOnly || isSwitchingMode || isSwitchingRecipe}
              sx={{ px: 0.5, minWidth: 28 }}
            >
              <ArrowDropDownIcon fontSize="small" />
            </Button>
          </ButtonGroup>
          <Menu
            anchorEl={saveMenuAnchor}
            open={Boolean(saveMenuAnchor)}
            onClose={handleSaveMenuClose}
          >
            <MenuItem
              onClick={() => {
                onToggleAutosave();
                handleSaveMenuClose();
              }}
            >
              <ListItemIcon>{autosaveEnabled ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
              <ListItemText>Autosave</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                onDiscardChanges();
                handleSaveMenuClose();
              }}
              disabled={!isModified}
            >
              <ListItemText>Discard Changes</ListItemText>
            </MenuItem>
          </Menu>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* CENTER SECTION: Run + Deploy + Editor Mode */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <ButtonGroup size="small" variant="contained" color="primary">
            <Button
              startIcon={
                isExecuting ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />
              }
              disabled={isExecuting || isSwitchingMode || isSwitchingRecipe}
              onClick={() => onRun(jobMode)}
            >
              {jobMode === 'runAsJob' ? 'Run as Job' : 'Run as Event'}
            </Button>
            <Button
              size="small"
              onClick={onRunMenuOpen}
              disabled={isSwitchingMode || isSwitchingRecipe}
              sx={{ px: 0.5, minWidth: 28 }}
            >
              <ArrowDropDownIcon fontSize="small" />
            </Button>
          </ButtonGroup>
          <Menu anchorEl={runMenuAnchor} open={Boolean(runMenuAnchor)} onClose={handleRunMenuClose}>
            <MenuItem
              onClick={() => {
                dispatch({ type: 'SET_JOB_MODE', payload: 'runAsJob' });
                handleRunMenuClose();
              }}
            >
              <ListItemIcon>
                {jobMode === 'runAsJob' ? <CheckIcon fontSize="small" /> : null}
              </ListItemIcon>
              <ListItemText primary="Run as Job" secondary="Execute in production" />
            </MenuItem>
            <MenuItem
              onClick={() => {
                dispatch({ type: 'SET_JOB_MODE', payload: 'runAsEvent' });
                handleRunMenuClose();
              }}
            >
              <ListItemIcon>
                {jobMode === 'runAsEvent' ? <CheckIcon fontSize="small" /> : null}
              </ListItemIcon>
              <ListItemText primary="Run as Event" secondary="Edit event context and run" />
            </MenuItem>
          </Menu>

          {selectedEnvironment && !isDeployedToSelectedEnv && (
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={onDeploy}
              disabled={isDeploying || isSwitchingMode || isSwitchingRecipe}
            >
              Deploy
            </Button>
          )}

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          {/* Editor Mode Toggle */}
          <ToggleButtonGroup
            size="small"
            value={editorMode}
            exclusive
            onChange={(_e, newMode) => newMode && !isSwitchingMode && onEditorModeChange(newMode)}
            disabled={isSwitchingMode || isSwitchingRecipe}
          >
            <ToggleButton value="yaml" disabled={isSwitchingMode || isSwitchingRecipe}>
              <Tooltip title={isSwitchingMode ? 'Switching mode...' : 'Code Editor'}>
                {isSwitchingMode && editorMode === 'visual' ? (
                  <CircularProgress size={16} />
                ) : (
                  <CodeIcon fontSize="small" />
                )}
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="visual" disabled={isSwitchingMode || isSwitchingRecipe}>
              <Tooltip title={isSwitchingMode ? 'Switching mode...' : 'Visual Editor'}>
                {isSwitchingMode && editorMode === 'yaml' ? (
                  <CircularProgress size={16} />
                ) : (
                  <VisualIcon fontSize="small" />
                )}
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          {/* View Toggles - Only enabled in YAML mode */}
          {/* eslint-disable no-nested-ternary -- UI tooltip logic naturally nests */}
          <Tooltip
            title={
              isSwitchingMode || isSwitchingRecipe
                ? 'Switching...'
                : editorMode === 'visual'
                  ? 'Available in YAML mode only'
                  : ''
            }
          >
            <span>
              <ToggleButton
                size="small"
                value="executionLog"
                selected={state.bottomPanelTabs.some((t) => t.type === 'executionLog')}
                onChange={onExecutionLogToggle}
                disabled={isSwitchingMode || isSwitchingRecipe}
              >
                <ListIcon fontSize="small" sx={{ mr: 0.5 }} />
                Log
              </ToggleButton>
            </span>
          </Tooltip>

          <Tooltip
            title={
              isSwitchingMode || isSwitchingRecipe
                ? 'Switching...'
                : editorMode === 'visual'
                  ? 'Available in YAML mode only'
                  : ''
            }
          >
            {/* eslint-enable no-nested-ternary */}
            <span>
              <ToggleButton
                size="small"
                value="compare"
                selected={showCompare}
                onChange={onCompareToggle}
                disabled={editorMode === 'visual' || isSwitchingMode || isSwitchingRecipe}
              >
                <CompareArrowsIcon fontSize="small" sx={{ mr: 0.5 }} />
                Compare
              </ToggleButton>
            </span>
          </Tooltip>
        </Box>

        <Box sx={{ flexGrow: 1, minWidth: 8 }} />

        {/* RIGHT SECTION: Template + Environment + Version + Tools */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {/* Template Dropdown (tech-support only) */}
          {showTemplateSelection && (
            <>
              <Select
                size="small"
                value={state.selectedTemplate || ''}
                onChange={(e) =>
                  dispatch({ type: 'SET_SELECTED_TEMPLATE', payload: e.target.value })
                }
                disabled={isSwitchingMode || isSwitchingRecipe}
                displayEmpty
                sx={{
                  minWidth: 100,
                  maxWidth: 140,
                  height: 28,
                  '& .MuiSelect-select': {
                    py: 0.25,
                    fontSize: '12px',
                  },
                }}
              >
                <MenuItem value="">Template</MenuItem>
                {state.availableTemplates
                  .slice()
                  .sort((a, b) => a.localeCompare(b))
                  .map((template) => (
                    <MenuItem key={template} value={template}>
                      {template.toUpperCase()}
                    </MenuItem>
                  ))}
              </Select>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            </>
          )}

          {/* Environment Dropdown */}
          <Select
            size="small"
            value={selectedEnvironment || ''}
            onChange={onEnvironmentChange}
            disabled={isSwitchingMode || isSwitchingRecipe}
            displayEmpty
            renderValue={(value) => {
              if (!value) return 'Environment';
              const env = environments.find((e) => e.id === value);
              return env?.name || value;
            }}
            sx={{
              minWidth: 100,
              maxWidth: 200,
              height: 28,
              '& .MuiSelect-select': {
                py: 0.25,
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
              },
            }}
          >
            <MenuItem value="">Environment</MenuItem>
            {[...environments]
              .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
              .map((env) => (
                <MenuItem
                  key={env.id}
                  value={env.id}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '2px',
                      bgcolor: env.color || '#404040',
                      flexShrink: 0,
                      border: env.id === selectedEnvironment ? '1px solid white' : 'none',
                      boxShadow:
                        env.id === selectedEnvironment ? '0 0 0 1px rgba(0,0,0,0.3)' : 'none',
                    }}
                  />
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {env.name}
                  </Box>
                  {env.deployedVersion != null && (
                    <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                      {`– Ver. ${env.deployedVersion} (deployed)`}
                    </Box>
                  )}
                </MenuItem>
              ))}
          </Select>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <Chip
            label={`v${currentVersion}`}
            size="small"
            onClick={onVersionsClick}
            clickable
            disabled={isSwitchingMode || isSwitchingRecipe}
            variant="outlined"
            sx={{ height: 24, fontSize: '12px' }}
          />

          <Button
            size="small"
            variant="outlined"
            onClick={onCheckInClick}
            disabled={isSwitchingMode || isSwitchingRecipe}
          >
            New Version
          </Button>

          <Tooltip title="More options">
            <IconButton
              size="small"
              onClick={onToolsMenuOpen}
              disabled={isSwitchingMode || isSwitchingRecipe}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={toolsMenuAnchor}
            open={Boolean(toolsMenuAnchor)}
            onClose={handleToolsMenuClose}
          >
            <MenuItem
              onClick={() => {
                onScheduleClick();
                handleToolsMenuClose();
              }}
            >
              <ListItemIcon>
                <ScheduleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Schedules</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                onMetadataToggle();
                handleToolsMenuClose();
              }}
              disabled={!selectedEnvironment}
            >
              <ListItemIcon>
                <StorageIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Metadata</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                onCommandPaletteClick();
                handleToolsMenuClose();
              }}
            >
              <ListItemIcon>
                <SearchIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Command Palette" secondary="Cmd+K" />
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </Box>
  );
};
